/* ─────────────────────────────────────────────────────────────────────────────
   RVP with QC — RTK Query slice (single data layer for the admin module).

   The backend contract (/api/qc/*) is frozen as OpenAPI BEFORE we wire real calls
   (TRD §4, Frontend-Prompt §7). Until then USE_MOCK is true and every endpoint
   resolves from the in-memory mock store (rvpQcMockData.js), so the admin UI is
   fully clickable — including the Courier QC Master CRUD wizard. When the contract
   lands: flip USE_MOCK to false; the real transport (shared axios helpers +
   apiConstants paths) is already wired and the mock response shapes match.

   Transport mirrors d2cApiSlice: shared get/post/putGeneric from constants.js
   (ENDPOINT = /api/ base). Responses are raw JSON (no GenericAPIResponse envelope).
   ──────────────────────────────────────────────────────────────────────────── */
import { createApi } from '@reduxjs/toolkit/query/react'
import { apiConstants, get, post, putGeneric, deleteGeneric } from '../common/constants'
import {
  mockListMerchants,
  mockGetMerchant,
  mockListParams,
  mockSaveMerchant,
  mockChangeRequest,
  mockGetQcMaster,
  mockSaveMasterParam,
  mockDeleteMasterParam,
  mockAddCourier,
  mockDeleteCourier,
  mockMapCourierParam,
} from '../pages/admin/rvp-qc/rvpQcMockData'

/* Flip to false once /api/qc/* is live. */
const USE_MOCK = true

const QC_MERCHANTS = apiConstants.QC_MERCHANTS            // 'qc/merchants'
const QC_PARAMS = apiConstants.QC_PARAMS                  // 'qc/params'
const QC_COURIER_MASTER = apiConstants.QC_COURIER_MASTER  // 'qc/courier-master'

/* Real backend paths (Frontend Integration Guide §2 + courier-QC-questions). Defined
   inline (not in shared constants.js) so the RVP-QC module stays self-contained. */
const QC_PARAMETER_MASTER = 'qc-parameter-master'            // GET /api/qc-parameter-master

/* axios call → RTK Query {data} | {error}. Returns the raw response body. */
const run = async (promise) => {
  try {
    const res = await promise
    return { data: res?.data }
  } catch (err) {
    const r = err?.response
    return { error: { status: r?.status, data: r?.data, message: r?.data?.message || err.message } }
  }
}
/* mock helpers in {data}/{error} shape RTK Query expects */
const mock = (value) => ({ data: value })
const mockRes = (res) => (res && res.error ? { error: { status: 'VALIDATION', message: res.error } } : { data: res })

export const rvpQcApiSlice = createApi({
  reducerPath: 'rvpQcApi',
  baseQuery: () => ({ data: undefined }), // unused — every endpoint defines its own queryFn
  tagTypes: ['Merchants', 'MerchantConfig', 'Params', 'QcMaster', 'CourierQc'],
  endpoints: (b) => ({
    // ── Merchant QC setups ────────────────────────────────────────────────
    listMerchants: b.query({
      queryFn: () => (USE_MOCK ? mock(mockListMerchants()) : run(get(QC_MERCHANTS))),
      providesTags: ['Merchants'],
    }),
    getMerchant: b.query({
      queryFn: (merchantId) =>
        USE_MOCK ? mock(mockGetMerchant(merchantId)) : run(get(`${QC_MERCHANTS}/${merchantId}`)),
      providesTags: (r, e, merchantId) => [{ type: 'MerchantConfig', id: merchantId }],
    }),
    saveMerchant: b.mutation({
      queryFn: (config) =>
        USE_MOCK ? mock(mockSaveMerchant(config)) : run(post(`${QC_MERCHANTS}/${config.id}`, config)),
      invalidatesTags: (r, e, config) => ['Merchants', { type: 'MerchantConfig', id: config?.id }],
    }),
    changeRequest: b.mutation({
      queryFn: ({ merchantId, note }) =>
        USE_MOCK
          ? mock(mockChangeRequest(merchantId, note))
          : run(post(`${QC_MERCHANTS}/${merchantId}/change-request`, { note })),
    }),

    // ── §3 Merchant QC config (LIVE) — per merchant OID ────────────────────
    // §3.1 GET /api/config/merchant/{id}/qc-return → config or empty body (never configured)
    merchantQcConfig: b.query({
      queryFn: (id) => run(get(`config/merchant/${id}/qc-return`)),
      providesTags: (r, e, id) => [{ type: 'MerchantConfig', id }],
    }),
    // §3.2 POST /api/config/merchant/{id}/qc-return → body { mode, standardParameters?, reasonMap? }
    saveMerchantQcConfig: b.mutation({
      queryFn: ({ id, body }) => run(post(`config/merchant/${id}/qc-return`, body)),
      invalidatesTags: (r, e, { id }) => [{ type: 'MerchantConfig', id }],
    }),

    // ── Master parameters (shared list merchants pick from) ────────────────
    listParams: b.query({
      queryFn: () => (USE_MOCK ? mock(mockListParams()) : run(get(QC_PARAMS))),
      providesTags: ['Params'],
    }),

    // ── §2.1 List QC parameter master (LIVE — first real-wired endpoint) ────
    // GET /api/qc-parameter-master?activeOnly=true → array of param objects with cpMap.
    qcParameterMaster: b.query({
      queryFn: ({ activeOnly = true } = {}) => run(get(QC_PARAMETER_MASTER, { activeOnly })),
      providesTags: ['Params'],
    }),
    // ── Courier QC parameters for a courier (LIVE) — fills the Step 2 mapping table ──
    // GET /api/qc-parameter-master/courier/{cpId}/options  (main backend, Bearer auth)
    courierQcQuestions: b.query({
      queryFn: async (cpId) => {
        try {
          const res = await get(`${QC_PARAMETER_MASTER}/courier/${cpId}/options`)
          const body = res?.data
          const arr = Array.isArray(body) ? body
            : Array.isArray(body?.result) ? body.result
              : Array.isArray(body?.data) ? body.data : []
          // normalise to the shape Step 2 consumes (tolerant of field-name variants).
          // This endpoint returns { qcParameterCode, apiField?, questionText, mandatory, questionCode }
          // — no inputType (Input type column stays "—"); questionText fills the Question column.
          const options = arr.map((x) => ({
            qcParameterCode: x.qcParameterCode ?? x.code ?? x.value,   // shown in the dropdown + stored as cpQcCode
            apiField: x.apiField ?? x.field,
            inputType: x.inputType ?? x.input ?? x.type,
            questionJson: x.questionJson ?? x.questionText ?? x.question ?? x.q,
            mandatory: x.mandatory,
          }))
          return { data: options }
        } catch (err) {
          const r = err?.response
          return { error: { status: r?.status, data: r?.data, message: r?.data?.message || err.message } }
        }
      },
      providesTags: (r, e, id) => [{ type: 'CourierQc', id }],
    }),

    // ── §2.3 Create a parameter (LIVE) — POST /api/qc-parameter-master ──────
    // body { code, description }. code is upper-cased server-side; duplicate → 400.
    createParameter: b.mutation({
      queryFn: (body) => run(post(QC_PARAMETER_MASTER, body)),
      invalidatesTags: ['Params'],
    }),
    // ── §2.4 Update / soft-delete (LIVE) — PUT /api/qc-parameter-master/{id} ─
    // Partial patch: only present fields change. Soft-delete by sending { isActive:false }.
    updateParameter: b.mutation({
      queryFn: ({ id, body }) => run(putGeneric(`${QC_PARAMETER_MASTER}/${id}`, body)),
      invalidatesTags: ['Params'],
    }),
    // ── §2.5 Add/replace a courier mapping — POST /api/qc-parameter-master/{id}/cp-map ─
    // body { cpId, cpQcCode }. Response = the updated param → write it into the §2.1 cache
    // directly (no refetch), so Step 2 + Step 3 update from a single call.
    addCpMap: b.mutation({
      queryFn: ({ paramId, cpId, cpQcCode }) => run(post(`${QC_PARAMETER_MASTER}/${paramId}/cp-map`, { cpId, cpQcCode })),
      // Optimistic: the Select's value is controlled straight off this cache. We deliberately do
      // NOT reconcile with the mutation's own response body afterwards — confirmed live that this
      // endpoint can echo back a document last written by a DIFFERENT user's concurrent edit
      // (another admin mapping the same courier at the same time), which silently overwrote our
      // just-saved pick with their older value. We already know exactly what we asked the server
      // to set, so a 200 just keeps the optimistic value; only a genuine failure rolls it back. A
      // real conflict (rare — two people editing the same courier's mapping at once) surfaces on
      // the next full page load instead of flickering the pick away immediately.
      async onQueryStarted({ paramId, cpId, cpQcCode }, { dispatch, queryFulfilled }) {
        const patch = dispatch(rvpQcApiSlice.util.updateQueryData('qcParameterMaster', { activeOnly: true }, (draft) => {
          const list = Array.isArray(draft) ? draft : (Array.isArray(draft?.data) ? draft.data : null)
          const p = list && list.find((x) => x.id === paramId)
          if (!p) return
          const map = p.cpMap || (p.cpMap = [])
          const i = map.findIndex((m) => String(m.cpId) === String(cpId))
          if (i >= 0) map[i] = { ...map[i], cpId, cpQcCode }
          else map.push({ cpId, cpQcCode })
        }))
        try { await queryFulfilled } catch (e) {
          patch.undo()
          console.error('[rvpQc] addCpMap failed — mapping was rolled back:', e)
        }
      },
    }),
    // ── §2.6 Remove a courier mapping — DELETE /api/qc-parameter-master/{id}/cp-map/{cpId} ─
    removeCpMap: b.mutation({
      queryFn: ({ paramId, cpId }) => run(deleteGeneric(`${QC_PARAMETER_MASTER}/${paramId}/cp-map/${cpId}`)),
      async onQueryStarted({ paramId, cpId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(rvpQcApiSlice.util.updateQueryData('qcParameterMaster', { activeOnly: true }, (draft) => {
          const list = Array.isArray(draft) ? draft : (Array.isArray(draft?.data) ? draft.data : null)
          const p = list && list.find((x) => x.id === paramId)
          if (p && p.cpMap) p.cpMap = p.cpMap.filter((m) => String(m.cpId) !== String(cpId))
        }))
        try { await queryFulfilled } catch (e) {
          patch.undo()
          console.error('[rvpQc] removeCpMap failed — mapping was rolled back:', e)
        }
      },
    }),

    // ── Courier QC Master (3-step wizard bundle + CRUD) ────────────────────
    qcMaster: b.query({
      queryFn: () => (USE_MOCK ? mock(mockGetQcMaster()) : run(get(QC_COURIER_MASTER))),
      providesTags: ['QcMaster'],
    }),
    saveMasterParam: b.mutation({
      // { mode: 'add'|'edit', id?, name, desc }
      queryFn: (body) =>
        USE_MOCK ? mockRes(mockSaveMasterParam(body)) : run(post(`${QC_PARAMS}`, body)),
      invalidatesTags: ['QcMaster', 'Params'],
    }),
    deleteMasterParam: b.mutation({
      queryFn: (id) =>
        USE_MOCK ? mockRes(mockDeleteMasterParam(id)) : run(post(`${QC_PARAMS}/${id}/delete`, {})),
      invalidatesTags: ['QcMaster', 'Params', 'Merchants'],
    }),
    addCourier: b.mutation({
      queryFn: (name) =>
        USE_MOCK ? mockRes(mockAddCourier(name)) : run(post(`${QC_COURIER_MASTER}/courier`, { name })),
      invalidatesTags: ['QcMaster'],
    }),
    deleteCourier: b.mutation({
      queryFn: (name) =>
        USE_MOCK ? mockRes(mockDeleteCourier(name)) : run(post(`${QC_COURIER_MASTER}/courier/${encodeURIComponent(name)}/delete`, {})),
      invalidatesTags: ['QcMaster'],
    }),
    mapCourierParam: b.mutation({
      // { courier, masterId, cqp }  (cqp '' = unmap)
      queryFn: ({ courier, masterId, cqp }) =>
        USE_MOCK ? mockRes(mockMapCourierParam(courier, masterId, cqp)) : run(post(`${QC_COURIER_MASTER}/${encodeURIComponent(courier)}/map`, { masterId, cqp })),
      invalidatesTags: ['QcMaster'],
    }),
  }),
})

export const {
  useListMerchantsQuery,
  useGetMerchantQuery,
  useSaveMerchantMutation,
  useChangeRequestMutation,
  useMerchantQcConfigQuery,
  useSaveMerchantQcConfigMutation,
  useListParamsQuery,
  useQcParameterMasterQuery,
  useCreateParameterMutation,
  useUpdateParameterMutation,
  useAddCpMapMutation,
  useRemoveCpMapMutation,
  useCourierQcQuestionsQuery,
  useQcMasterQuery,
  useSaveMasterParamMutation,
  useDeleteMasterParamMutation,
  useAddCourierMutation,
  useDeleteCourierMutation,
  useMapCourierParamMutation,
} = rvpQcApiSlice
